import React, { useState, useEffect } from "react";
import { FaSun, FaMoon, FaBars, FaTimes } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../assets/images/logo.svg";

const Navbar = ({ darkMode, toggleDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleNavigation = (href, name) => {
    setIsOpen(false);
    
    // Always scroll to top when navigating
    window.scrollTo(0, 0);
    
    // Navigate to the href
    navigate(href);
  };

  const navItems = [
    { name: "Home", href: "/" },
    { name: "Projects", href: "/#projects" },
    { name: "Experience", href: "/#experience" },
    { name: "Education", href: "/#education" },
    { name: "Contact", href: "/#contact" },
    { name: "Blog", href: "/blog" },
  ];

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/" && !location.hash;
    if (href === "/blog") return location.pathname === "/blog";
    if (href.startsWith("/#")) {
      const sectionId = href.substring(2);
      return location.pathname === "/" && location.hash === `#${sectionId}`;
    }
    return location.pathname === href;
  };

return (
  <nav
    className={`fixed inset-x-0 top-0 w-full overflow-x-hidden z-50 transition-all duration-300 ${
      scrolled
        ? "bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-lg py-3"
        : "bg-transparent py-6"
    }`}
  >
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <a href="#home" className="flex items-center space-x-2">
          <div
            role="img"
            aria-label="Logo"
            className="w-[47px] h-[47px] md:w-[50px] md:h-[50px] gradient-text-mask"
            style={{
              WebkitMaskImage: `url(${Logo})`,
              maskImage: `url(${Logo})`,
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }}
          />
        </a>
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavigation(item.href, item.name)}
              className={`text-muted hover:text-cyber-green transition-colors duration-300 font-medium relative group ${
                isActive(item.href) ? "text-cyber-green" : ""
              }`}
            >
              {item.name}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-cyber-green transition-all duration-300 ${
                  isActive(item.href)
                    ? "w-full"
                    : "w-0 group-hover:w-full"
                }`}
              />
            </button>
          ))}

          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg glass-effect hover:scale-110 transition-all duration-300"
            aria-label={
              darkMode
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
          >
            {darkMode ? (
              <FaSun className="text-amber-400" />
            ) : (
              <FaMoon className="text-cyber-blue" />
            )}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2 rounded-lg glass-effect"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <FaTimes className="text-2xl" />
          ) : (
            <FaBars className="text-2xl" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden w-full mt-6">
          <div className="w-full py-6 glass-effect rounded-xl animate-fadeIn overflow-hidden">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href, item.name)}
                  className={`w-full text-left text-muted hover:text-cyber-green transition-colors duration-300 font-medium py-2 px-4 hover:bg-cyber-green/5 rounded-lg ${
                    isActive(item.href)
                      ? "text-cyber-green bg-cyber-green/5"
                      : ""
                  }`}
                >
                  {item.name}
                </button>
              ))}

              <div className="pt-4 border-t border-gray-200 dark:border-white/10">
                <button
                  onClick={toggleDarkMode}
                  className="flex items-center justify-center w-full p-2 rounded-lg glass-effect"
                >
                  {darkMode ? (
                    <>
                      <FaSun className="text-amber-400 mr-2" />
                      <span>Light Mode</span>
                    </>
                  ) : (
                    <>
                      <FaMoon className="text-cyber-blue mr-2" />
                      <span>Dark Mode</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </nav>
);
};

export default Navbar;